import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { Dialog, DialogContent } from '@mui/material';
import { RenderFields } from './RenderFields';
import EmployeeAccordion from './EmployeeAccordion';
import { findSectionById } from '../../utils/employeeModalUtils';
import useAddEmployeeForm from '../../hooks/useAddEmployeeForm';
import { FieldsList } from '../../config/employeeFieldsConfig';
import FullscreenIcon from '@mui/icons-material/Fullscreen';
import FullscreenExitIcon from '@mui/icons-material/FullscreenExit';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';

const AddEmployeeModal = ({ open, handleClose, refreshData }) => {
  const {
    formData,
    handleInputChange,
    handleSubmit,
    validationErrors,
  } = useAddEmployeeForm(handleClose);
  const [isExpanded, setIsExpanded] = useState(false);

  const saveEmployee = async (e) => {
    e.preventDefault();
    const success = await handleSubmit();
    if (success) {
      refreshData();
      handleClose();
    }
  };

  const handleModalClose = () => {
    handleClose();
  };

  return (
    <>
      <Dialog
        open={open}
        onClose={handleModalClose}
        maxWidth={false}
        PaperProps={{
          style: {
            width: isExpanded ? '90%' : '1000px',
            maxHeight: '90vh',
          },
        }}
        fullWidth={true}
        style={{ zIndex: 1400 }}
      >
        {/* Header Section */}
        <div className="sticky top-0 bg-white z-10">
          <div className="flex justify-between items-center p-4">
            <div className="flex items-center space-x-4 flex-grow">
              <button
                onClick={handleModalClose}
                className="p-2 rounded-full hover:bg-gray-200 transition"
                aria-label="Back"
              >
                <ArrowBackIcon />
              </button>
              <h2 className="text-2xl font-bold text-left">Add Employee</h2>
            </div>

            {/* Expand/Shrink Button */}
            <button
              onClick={() => setIsExpanded(!isExpanded)}
              className="p-2 rounded-full hover:bg-gray-200 transition"
              aria-label={isExpanded ? 'Shrink Modal' : 'Expand Modal'}
            >
              {isExpanded ? <FullscreenExitIcon /> : <FullscreenIcon />}
            </button>
          </div>

          {/* Action Buttons */}
          <div className="flex space-x-4 p-4">
            <button
              type="button"
              onClick={saveEmployee}
              className="px-6 py-2 bg-yellow-500 font-bold rounded-md hover:bg-yellow-600 text-sss-blue-sel transition duration-300 ease-in-out"
            >
              Save
            </button>
          </div>
        </div>

        {/* Form Section */}
        <DialogContent dividers>
          <form onSubmit={saveEmployee}>
            <div className="space-y-4">

              {/* Personal Info Section */}
              <EmployeeAccordion label="Personal Info">
                <div className={`flex ${isExpanded ? 'flex-row' : ''} w-5/6 place-self-center gap-4`}>
                  <div className="w-full">
                    <RenderFields
                      fields={findSectionById(FieldsList, 'personalInfo')?.fields || []}
                      formData={formData}
                      handleChange={handleInputChange}
                      validationErrors={validationErrors}
                    />
                  </div>
                  <div className="w-full">
                    <RenderFields
                      fields={findSectionById(FieldsList, 'accountInfo')?.fields || []}
                      formData={formData}
                      handleChange={handleInputChange}
                      validationErrors={validationErrors}
                    />
                  </div>
                </div>
              </EmployeeAccordion>

              {/* Login Info Section */}
              <EmployeeAccordion label="Login Info">
                <div className={`flex ${isExpanded ? 'flex-row' : ''} w-5/6 place-self-center gap-4`}>
                  <div className="w-full">
                    <RenderFields
                      fields={findSectionById(FieldsList, 'usernameInfo')?.fields || []}
                      formData={formData}
                      handleChange={handleInputChange}
                      validationErrors={validationErrors}
                    />
                  </div>
                  <div className="w-full">
                    <RenderFields
                      fields={findSectionById(FieldsList, 'passwordInfo')?.fields || []}
                      formData={formData}
                      handleChange={handleInputChange}
                      validationErrors={validationErrors}
                    />
                  </div>
                </div>
              </EmployeeAccordion>

              {/* Address Info Section */}
              <EmployeeAccordion label="Address Info">
                <div className={`flex ${isExpanded ? 'flex-row' : ''} w-5/6 place-self-center gap-4`}>
                  <div className="w-full">
                    <RenderFields
                      fields={findSectionById(FieldsList, 'addressInfo')?.fields || []}
                      formData={formData}
                      handleChange={handleInputChange}
                      validationErrors={validationErrors}
                    />
                  </div>
                  <div className="w-full">
                    <RenderFields
                      fields={findSectionById(FieldsList, 'datesInfo')?.fields || []}
                      formData={formData}
                      handleChange={handleInputChange}
                      validationErrors={validationErrors}
                    />
                  </div>
                </div>
              </EmployeeAccordion>

              {/* Contact Info Section */}
              <EmployeeAccordion label="Contact Info">
                <div className={`flex ${isExpanded ? 'flex-row' : ''} w-5/6 place-self-center gap-4`}>
                  <div className="w-full">
                    <RenderFields
                      fields={findSectionById(FieldsList, 'contactInfo')?.fields || []}
                      formData={formData}
                      handleChange={handleInputChange}
                      validationErrors={validationErrors}
                    />
                  </div>
                  <div className="w-full">
                    <RenderFields
                      fields={findSectionById(FieldsList, 'jobInfo')?.fields || []}
                      formData={formData}
                      handleChange={handleInputChange}
                      validationErrors={validationErrors}
                    />
                  </div>
                </div>
              </EmployeeAccordion>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </>
  );
};

AddEmployeeModal.propTypes = {
  open: PropTypes.bool.isRequired,
  handleClose: PropTypes.func.isRequired,
  refreshData: PropTypes.func.isRequired,
};
export default AddEmployeeModal;
